import { snackbar } from "@bcmi-labs/art-ui/components"
import { noop } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import * as v from "valibot"

import { addMemberViaCode } from "@/api/classroom"
import { $rqViewsApiClient } from "@/api/groups"
import { query } from "@/api/query"
import { $usersApiClient } from "@/api/users"
import { getXOrganizationHeaders } from "@/api/utils"
import { routeGuard } from "@/lib/routing/beforeLoad"
import { useModalStore } from "@/store/modals"

const searchSchema = v.object({
  kind: v.optional(v.string()),
  join: v.optional(v.string()),
  "join-space": v.optional(v.string()),
  "join-code": v.optional(v.string()),
  "get-started": v.optional(v.string()),
  renewal_id: v.optional(v.string()),
  space_id: v.optional(v.string())
})

export const Route = createFileRoute("/_authlayout/_sidebar/")({
  validateSearch: searchSchema,
  beforeLoad: async ({ context, search }) => {
    await routeGuard(context, { allowMinor: true })

    // Parse global query params, but only if the app is not running in a custom domain.
    if (!context.authClient.getCustomization() && Object.keys(search).length > 0) {
      const spaces = await context.queryClient.query({ ...query.space.list, staleTime: "static" })

      const firstKey = Object.keys(search)[0] as keyof typeof search

      // Get the first key, because for now we're only planning to use one query param at a time.
      switch (firstKey) {
        // - If the user doesn't have this kit on the private space then redirect to the registration page with the code step on the private space
        // The user can get redirected here from a course website when he doesn't own the kit: redirect to registration page, code step.
        case "kind": {
          redirect({ to: "/registration", throw: true })

          break
        }
        // Select the space provided in the query param (this isn't actually a join event)
        case "join": {
          const join = search.join as string

          // Check if user is already member of that space
          if (spaces.some(space => space.id === join)) {
            context.spaceStorage.setSpace(join)
          }
          break
        }
        // Open the joinspace modal for code input
        case "join-space": {
          useModalStore.getState().setJoinSpaceFlowStep("join")
          break
        }
        // Start the "Get started" flow
        case "get-started":
          useModalStore.getState().setGetStartedFlowStep("start")
          break
        // Directly join a space
        case "join-code": {
          const joinCode = search["join-code"] as string

          // If there is no join code, just open the join space modal
          if (!joinCode) {
            useModalStore.getState().setJoinSpaceFlowStep("start")
            return
          }

          try {
            const resp = await $usersApiClient.GET("/users/v1/organizations/validate", {
              params: { query: { code: joinCode } }
            })

            if (resp.error || !resp.data.organization_id || !resp.data.available) {
              break
            }

            const { organization_id } = resp.data

            if (spaces.some(space => space.id === organization_id)) {
              context.spaceStorage.setSpace(organization_id)
              snackbar({ message: "You are already a member of this space." })
              break
            }

            const { status } = await addMemberViaCode({ code: joinCode })

            if (status === 201) {
              // Refresh the space list, we just joined a new space
              await context.queryClient.refetchQueries(query.space.list)
              // ! Nasty race condition. Set space after 500ms, so the sidebar has time to update
              setTimeout(() => {
                context.spaceStorage.setSpace(organization_id)
                useModalStore.getState().setJoinSpaceFlowStep("end")
              }, 500)
            }
          } catch (error) {
            console.error(error)
            useModalStore.getState().setJoinSpaceFlowStep("error")
          }
          break
        }
        case "renewal_id": {
          const renewal_id = search.renewal_id as string

          if (spaces.some(space => space.id === renewal_id)) {
            context.spaceStorage.setSpace(renewal_id)
            snackbar({ message: "Plan activated successfully.", variant: "success" })
          }
          break
        }
        // DS store flow -> post purchase entry point
        case "space_id": {
          const id = search.space_id as string

          // Look in the list of spaces if the space exists, and if it is a pro space
          const foundSpace = spaces.find(sp => sp.id === id)
          if (foundSpace) context.spaceStorage.setSpace(id)
          if (foundSpace && foundSpace.space === "pro") {
            useModalStore.getState().setProSpaceFlowStep("name")
          }

          break
        }
        default:
          break
      }

      // Purge the queryparams from the URL
      redirect({
        from: "/",
        search: {},
        throw: true
      })
    }
  },
  loader: ({ context: { queryClient, spaceStorage } }) => {
    const space = spaceStorage.getSpace()

    queryClient.query(query.space.members(space)).catch(noop)

    queryClient
      .query(
        $rqViewsApiClient.queryOptions("get", "/groups/v1/devices", {
          params: { header: getXOrganizationHeaders(space) }
        })
      )
      .catch(noop)

    queryClient
      .query(
        $rqViewsApiClient.queryOptions("get", "/groups/v1/things", {
          params: { header: getXOrganizationHeaders(space) }
        })
      )
      .catch(noop)

    queryClient
      .query(
        $rqViewsApiClient.queryOptions("get", "/groups/v1/dashboards", {
          params: { header: getXOrganizationHeaders(space) }
        })
      )
      .catch(noop)
  },
  head: () => ({
    meta: [
      {
        title: "Home | Arduino Cloud"
      }
    ]
  })
})
